import React from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Typography,
  Stack,
} from '@mui/material';
import {
  EVENT_LOCATION_KEYS,
  EventLocationKey,
  EventLocationOverride,
} from '../../types/event';

export const LOCATION_LABELS: Record<EventLocationKey, string> = {
  LivingstonDen: 'Livingston Den',
  AberdeenDen: 'Aberdeen Den',
  GlasgowPopUp: 'Glasgow Pop Up',
  FifePopUp: 'Fife Pop Up',
  Outing: 'Outing (one-off venue)',
};

export interface LocationFieldValue {
  locationKey: EventLocationKey | null;
  override: EventLocationOverride;
}

interface LocationFieldProps {
  value: LocationFieldValue;
  onChange: (next: LocationFieldValue) => void;
  error?: string | null;
}

const EMPTY_OVERRIDE: EventLocationOverride = {
  placeName: '',
  addressLine1: '',
  postcode: '',
  lat: null,
  lng: null,
};

const LocationField: React.FC<LocationFieldProps> = ({ value, onChange, error }) => {
  const isOuting = value.locationKey === 'Outing';

  const handleKeyChange = (next: string) => {
    const nextKey = (next || null) as EventLocationKey | null;
    // When switching off Outing, blank the override so we don't ship stale data.
    onChange({
      locationKey: nextKey,
      override: nextKey === 'Outing' ? value.override : { ...EMPTY_OVERRIDE },
    });
  };

  const updateOverride = <K extends keyof EventLocationOverride>(
    field: K,
    next: EventLocationOverride[K],
  ) => {
    onChange({
      ...value,
      override: { ...value.override, [field]: next },
    });
  };

  return (
    <Box>
      <TextField
        fullWidth
        select
        label="Location"
        value={value.locationKey ?? ''}
        onChange={(e) => handleKeyChange(e.target.value)}
        error={!!error}
        helperText={error ?? 'Pick a permanent venue or "Outing" for a one-off location.'}
      >
        <MenuItem value="">— select —</MenuItem>
        {EVENT_LOCATION_KEYS.map((key) => (
          <MenuItem key={key} value={key}>
            {LOCATION_LABELS[key]}
          </MenuItem>
        ))}
      </TextField>

      {isOuting && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.default',
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Outing details
          </Typography>
          <Stack spacing={2}>
            <TextField
              fullWidth
              required
              label="Place name"
              value={value.override.placeName}
              onChange={(e) => updateOverride('placeName', e.target.value)}
              helperText="Required for outings"
            />
            <TextField
              fullWidth
              label="Address line 1"
              value={value.override.addressLine1 ?? ''}
              onChange={(e) => updateOverride('addressLine1', e.target.value)}
            />
            <TextField
              fullWidth
              label="Postcode"
              value={value.override.postcode ?? ''}
              onChange={(e) => updateOverride('postcode', e.target.value)}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                type="number"
                label="Latitude (optional)"
                inputProps={{ step: '0.000001' }}
                value={value.override.lat ?? ''}
                onChange={(e) =>
                  updateOverride('lat', e.target.value === '' ? null : Number(e.target.value))
                }
              />
              <TextField
                fullWidth
                type="number"
                label="Longitude (optional)"
                inputProps={{ step: '0.000001' }}
                value={value.override.lng ?? ''}
                onChange={(e) =>
                  updateOverride('lng', e.target.value === '' ? null : Number(e.target.value))
                }
              />
            </Stack>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default LocationField;
